import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, OnDestroy, signal } from '@angular/core';
import { EInvoiceViewerSearchHandler } from '../../interfaces/einvoice-viewer-search-handler.interface';
import { EInvoiceViewerSearchOptions } from '../../interfaces/einvoice-viewer-search-options.interface';
import { EinvoiceViewerSearchService } from '../../services/einvoice-viewer-search.service';
import { EinvoiceViewerContextService } from '../../services/einvoice-viewer-context.service';
import { getEmbeddedContentPreview } from '../../utils/embedded-content-policy';
import { EInvoiceFieldSelection } from '../../utils/provenance-index';
import { matchesSearchTexts, normalizeSearchText } from '../../utils/viewer-search.util';

interface XmlLine {
  id: string;
  lineNumber: number;
  tokens: XmlToken[];
  rawText: string;
  path?: string;
  selectable: boolean;
}

interface XmlToken {
  text: string;
  kind: 'indent' | 'punctuation' | 'tag' | 'attribute-name' | 'attribute-value' | 'text' | 'hidden';
}

@Component({
  selector: 'app-einvoice-xml-view',
  templateUrl: './xml-view.component.html',
  styleUrls: ['./xml-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class EinvoiceXmlViewComponent implements EInvoiceViewerSearchHandler, OnDestroy {
  private readonly einvoiceViewerContextService = inject(EinvoiceViewerContextService);
  private readonly einvoiceViewerSearchService = inject(EinvoiceViewerSearchService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private lastScrolledSourcePath: string | undefined;

  readonly searchOptions = signal<EInvoiceViewerSearchOptions>({ query: '', exactMatch: false });
  readonly activeSearchResultIndex = signal(0);
  readonly copiedXml = signal(false);
  readonly copiedLineId = signal<string | undefined>(undefined);
  private copiedXmlResetHandle: ReturnType<typeof setTimeout> | undefined;
  private copiedLineResetHandle: ReturnType<typeof setTimeout> | undefined;
  readonly xmlFileName = this.einvoiceViewerContextService.xmlFileName;
  readonly lines = computed(() => formatXmlLines(this.einvoiceViewerContextService.rawXml()));
  readonly sourceLineCount = computed(() => new Set(this.lines()
    .filter(line => line.selectable && line.path)
    .map(line => line.path)).size);
  readonly selectedLinePaths = computed(() => new Set(this.einvoiceViewerContextService.selectedEntries()
    .map(entry => entry.src)
    .filter((sourcePath): sourcePath is string => sourcePath !== undefined)
    .map(sourcePath => getElementSourcePath(sourcePath))));
  readonly entriesByLinePath = computed(() => {
    const entriesByLinePath = new Map<string, EInvoiceFieldSelection[]>();
    for (const [sourcePath, entries] of this.einvoiceViewerContextService.provenanceIndex().bySourcePath.entries()) {
      const linePath = getElementSourcePath(sourcePath);
      entriesByLinePath.set(linePath, [...(entriesByLinePath.get(linePath) ?? []), ...entries]);
    }

    return entriesByLinePath;
  });
  readonly lineTermsByPath = computed(() => {
    const lineTermsByPath = new Map<string, string>();
    for (const [linePath, entries] of this.entriesByLinePath().entries()) {
      const terms = entries
        .map(entry => entry.term)
        .filter((term): term is string => term !== undefined)
        .filter((term, index, allTerms) => allTerms.indexOf(term) === index)
        .join(', ');

      if (terms.length > 0) {
        lineTermsByPath.set(linePath, terms);
      }
    }

    return lineTermsByPath;
  });
  readonly lineTermTextsByPath = computed(() => {
    const lineTermsByPath = new Map<string, string[]>();
    for (const [linePath, entries] of this.entriesByLinePath().entries()) {
      const terms = entries
        .map(entry => entry.term)
        .filter((term): term is string => term !== undefined)
        .filter((term, index, allTerms) => allTerms.indexOf(term) === index);

      if (terms.length > 0) {
        lineTermsByPath.set(linePath, terms);
      }
    }

    return lineTermsByPath;
  });
  readonly searchResults = computed(() => {
    const searchOptions = this.searchOptions();
    if (!normalizeSearchText(searchOptions.query)) {
      return [];
    }

    return this.lines()
      .filter(line => {
        const lineTerms = line.path ? this.lineTermTextsByPath().get(line.path) ?? [] : [];
        const tokenTexts = line.tokens
          .filter(token => token.kind === 'tag' || token.kind === 'attribute-value' || token.kind === 'text')
          .map(token => token.text);
        return matchesSearchTexts([line.rawText, ...lineTerms, ...tokenTexts], searchOptions);
      })
      .map(line => line.id);
  });
  readonly searchResultIds = computed(() => new Set(this.searchResults()));
  readonly currentSearchResultId = computed(() => this.searchResults()[this.activeSearchResultIndex()]);
  readonly activeLinePath = computed(() => {
    const activeModelPath = this.einvoiceViewerContextService.activeModelPath();
    const activeSourcePath = activeModelPath
      ? this.einvoiceViewerContextService.provenanceIndex().byModelPath.get(activeModelPath)?.src
      : undefined;

    return activeSourcePath ? getElementSourcePath(activeSourcePath) : undefined;
  });
  readonly copiedLineIdValue = this.copiedLineId.asReadonly();
  readonly matchCount = computed(() => this.searchResults().length);
  readonly activeMatchIndex = computed(() => this.searchResults().length === 0 ? -1 : this.activeSearchResultIndex());

  constructor() {
    this.einvoiceViewerSearchService.registerHandler('xml', this);

    effect(() => {
      const activeModelPath = this.einvoiceViewerContextService.activeModelPath();
      const activeSourcePath = activeModelPath
        ? this.einvoiceViewerContextService.provenanceIndex().byModelPath.get(activeModelPath)?.src
        : undefined;
      if (!activeSourcePath || this.lastScrolledSourcePath === activeSourcePath) {
        return;
      }

      this.lastScrolledSourcePath = activeSourcePath;
      requestAnimationFrame(() => scrollSourceLine(this.elementRef.nativeElement, activeSourcePath));
    });
  }

  ngOnDestroy(): void {
    this.einvoiceViewerSearchService.unregisterHandler('xml', this);
  }

  select(line: XmlLine, event: MouseEvent): void {
    if (!line.path) {
      return;
    }

    const entries = this.getEntriesForLine(line.path);
    if (!entries || entries.length === 0) {
      return;
    }

    event.stopPropagation();
    this.einvoiceViewerContextService.select(entries[0], event.ctrlKey || event.metaKey);
  }

  setSearchQuery(options: EInvoiceViewerSearchOptions): void {
    this.searchOptions.set(options);
    this.activeSearchResultIndex.set(0);
  }

  nextSearchResult(): void {
    this.moveSearchResult(1);
  }

  previousSearchResult(): void {
    this.moveSearchResult(-1);
  }

  scrollToActiveResult(): void {
    const currentSearchResultId = this.currentSearchResultId();
    if (!currentSearchResultId) {
      return;
    }

    requestAnimationFrame(() => scrollElementWithAttribute(this.elementRef.nativeElement, 'data-line-id', currentSearchResultId));
  }

  copyXml(): void {
    void navigator.clipboard?.writeText(this.einvoiceViewerContextService.rawXml() ?? '');
    this.copiedXml.set(true);
    clearTimeout(this.copiedXmlResetHandle);
    this.copiedXmlResetHandle = setTimeout(() => this.copiedXml.set(false), 1100);
  }

  copyLine(line: XmlLine, event: MouseEvent): void {
    event.stopPropagation();
    void navigator.clipboard?.writeText(line.rawText.trim());
    this.copiedLineId.set(line.id);
    clearTimeout(this.copiedLineResetHandle);
    this.copiedLineResetHandle = setTimeout(() => this.copiedLineId.set(undefined), 1100);
  }

  private getEntriesForLine(linePath: string): EInvoiceFieldSelection[] {
    return this.entriesByLinePath().get(linePath) ?? [];
  }

  private moveSearchResult(direction: 1 | -1): void {
    const searchResults = this.searchResults();
    if (searchResults.length === 0) {
      return;
    }

    const nextIndex = (this.activeSearchResultIndex() + direction + searchResults.length) % searchResults.length;
    this.activeSearchResultIndex.set(nextIndex);
    this.scrollToActiveResult();
  }
}

function sourcePathMatchesLine(sourcePath: string, linePath: string): boolean {
  return sourcePath === linePath || sourcePath.startsWith(`${linePath}/@`);
}

function getElementSourcePath(sourcePath: string): string {
  const attributeIndex = sourcePath.lastIndexOf('/@');
  return attributeIndex === -1 ? sourcePath : sourcePath.slice(0, attributeIndex);
}

function scrollSourceLine(root: HTMLElement, sourcePath: string): void {
  const element = Array.from(root.querySelectorAll<HTMLElement>('[data-source-path]'))
    .find(item => {
      const linePath = item.getAttribute('data-source-path');
      return linePath !== null && sourcePathMatchesLine(sourcePath, linePath);
    });
  element?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
}

function scrollElementWithAttribute(root: HTMLElement, attributeName: string, attributeValue: string): void {
  const element = Array.from(root.querySelectorAll<HTMLElement>(`[${attributeName}]`))
    .find(item => item.getAttribute(attributeName) === attributeValue);
  element?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
}

function formatXmlLines(xml: string | undefined): XmlLine[] {
  if (!xml) {
    return [];
  }

  const document = new DOMParser().parseFromString(xml, 'application/xml');
  if (document.getElementsByTagName('parsererror').length > 0 || !document.documentElement) {
    return xml.split(/\r?\n/).map((line, index) => ({
      id: `raw:${index}`,
      lineNumber: index + 1,
      tokens: [{ text: line, kind: 'text' }],
      rawText: line,
      selectable: false
    }));
  }

  const lines: XmlLine[] = [];
  renderElement(document.documentElement, '', 0, lines);
  return lines.map((line, index) => ({ ...line, lineNumber: index + 1 }));
}

function renderElement(element: Element, parentPath: string, depth: number, lines: XmlLine[]): void {
  const path = `${parentPath}/${formatClarkName(element)}[${getSiblingIndex(element)}]`;
  const childElements = Array.from(element.children);
  const text = getDirectText(element);
  const startLines = formatStartTagLines(element, depth);

  if (childElements.length === 0 && startLines.length === 1) {
    const collapsedText = collapseXmlText(element, text);
    const tokens = [
      ...startLines[0],
      { text: collapsedText, kind: collapsedText === text ? 'text' : 'hidden' } as XmlToken,
      ...formatEndTagTokens(element)
    ];
    lines.push({
      id: `${path}:inline`,
      lineNumber: 0,
      tokens,
      rawText: tokensToText(tokens),
      path,
      selectable: true
    });
    return;
  }

  startLines.forEach((tokens, index) => {
    lines.push({
      id: `${path}:start:${index}`,
      lineNumber: 0,
      tokens,
      rawText: tokensToText(tokens),
      path,
      selectable: true
    });
  });
  if (text) {
    const collapsedText = collapseXmlText(element, text);
    const textTokens: XmlToken[] = [
      { text: indent(depth + 1), kind: 'indent' },
      { text: collapsedText, kind: collapsedText === text ? 'text' : 'hidden' }
    ];
    lines.push({ id: `${path}:text`, lineNumber: 0, tokens: textTokens, rawText: tokensToText(textTokens), path, selectable: true });
  }

  for (const child of childElements) {
    renderElement(child, path, depth + 1, lines);
  }

  const endTokens = [{ text: indent(depth), kind: 'indent' } as XmlToken, ...formatEndTagTokens(element)];
  lines.push({ id: `${path}:end`, lineNumber: 0, tokens: endTokens, rawText: tokensToText(endTokens), path, selectable: true });
}

function formatStartTagLines(element: Element, depth: number): XmlToken[][] {
  const attributes = Array.from(element.attributes);
  if (!shouldWrapStartTagAttributes(element, attributes, depth)) {
    return [[
      { text: indent(depth), kind: 'indent' },
      { text: '<', kind: 'punctuation' },
      { text: element.tagName, kind: 'tag' },
      ...formatInlineAttributeTokens(attributes),
      { text: '>', kind: 'punctuation' }
    ]];
  }

  return [
    [
      { text: indent(depth), kind: 'indent' },
      { text: '<', kind: 'punctuation' },
      { text: element.tagName, kind: 'tag' }
    ],
    ...attributes.map((attribute, index) => [
      { text: indent(depth + 1), kind: 'indent' } as XmlToken,
      ...formatAttributeTokens(attribute),
      ...(index === attributes.length - 1 ? [{ text: '>', kind: 'punctuation' } as XmlToken] : [])
    ])
  ];
}

function formatEndTagTokens(element: Element): XmlToken[] {
  return [
    { text: '</', kind: 'punctuation' },
    { text: element.tagName, kind: 'tag' },
    { text: '>', kind: 'punctuation' }
  ];
}

function shouldWrapStartTagAttributes(element: Element, attributes: Attr[], depth: number): boolean {
  if (attributes.length === 0) {
    return false;
  }

  const inlineLength = tokensToText([
    { text: indent(depth), kind: 'indent' },
    { text: '<', kind: 'punctuation' },
    { text: element.tagName, kind: 'tag' },
    ...formatInlineAttributeTokens(attributes),
    { text: '>', kind: 'punctuation' }
  ]).length;

  return attributes.length > 1 && (inlineLength > 120 || attributes.some(attribute => attribute.name.startsWith('xmlns')));
}

function formatInlineAttributeTokens(attributes: Attr[]): XmlToken[] {
  return attributes.flatMap(attribute => [
    { text: ' ', kind: 'punctuation' } as XmlToken,
    ...formatAttributeTokens(attribute)
  ]);
}

function formatAttributeTokens(attribute: Attr): XmlToken[] {
  return [
    { text: attribute.name, kind: 'attribute-name' } as XmlToken,
    { text: '="', kind: 'punctuation' } as XmlToken,
    { text: attribute.value, kind: 'attribute-value' } as XmlToken,
    { text: '"', kind: 'punctuation' } as XmlToken
  ];
}

function collapseXmlText(element: Element, text: string): string {
  const attributes = attributesToRecord(element.attributes);
  const preview = getEmbeddedContentPreview(text, {
    Content: text,
    mimeCode: attributes['mimeCode'],
    filename: attributes['filename'],
    encodingCode: attributes['encodingCode']
  });

  return preview?.displayValue ?? text;
}

function getDirectText(element: Element): string {
  return Array.from(element.childNodes)
    .filter(node => node.nodeType === Node.TEXT_NODE)
    .map(node => node.textContent ?? '')
    .join('')
    .trim();
}

function getSiblingIndex(element: Element): number {
  let index = 1;
  let sibling = element.previousElementSibling;
  while (sibling) {
    if (sibling.namespaceURI === element.namespaceURI && sibling.localName === element.localName) {
      index++;
    }
    sibling = sibling.previousElementSibling;
  }
  return index;
}

function formatClarkName(element: Element): string {
  return `{${element.namespaceURI ?? ''}}${element.localName}`;
}

function attributesToRecord(attributes: NamedNodeMap): Record<string, string> {
  return Array.from(attributes).reduce<Record<string, string>>((result, attribute) => {
    result[attribute.name] = attribute.value;
    return result;
  }, {});
}

function indent(depth: number): string {
  return '  '.repeat(depth);
}

function tokensToText(tokens: XmlToken[]): string {
  return tokens.map(token => token.text).join('');
}